<template>
    <PageHeader :crumbs="[{ label: $t('portal.tickets.title'), to: { name: 'portal.tickets' } }, { label: ticket.subject || '...' }]" />

    <div class="tw-p-2 tw-space-y-4" v-if="!loading">
        <div class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-p-5">
            <div class="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-flex-wrap">
                <div>
                    <h1 class="tw-text-xl tw-font-bold tw-text-gray-800">{{ ticket.subject }}</h1>
                    <p class="tw-text-sm tw-text-gray-400 tw-mt-1">{{ fmt.datetime(ticket.created_at) }}</p>
                </div>
                <TicketStatusBadge :status="ticket.status" />
            </div>
        </div>

        <div class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-p-5">
            <TicketThread :messages="ticket.messages || []" :mine-is-admin="false" />

            <div v-if="ticket.status !== 'closed'" class="tw-mt-5 tw-pt-4 tw-border-t tw-border-solid">
                <FormField v-model="reply" type="textarea" :placeholder="$t('portal.tickets.reply-placeholder')" hide-errors />
                <div class="tw-flex tw-justify-end tw-mt-2">
                    <a-button :disabled="sending || !reply.trim()" :loading="sending" left-icon="solar:plain-bold-duotone" @click="sendReply">
                        {{ $t('portal.tickets.send-reply') }}
                    </a-button>
                </div>
            </div>
            <p v-else class="tw-mt-5 tw-pt-4 tw-border-t tw-border-solid tw-text-center tw-text-sm tw-text-gray-400">
                {{ $t('portal.tickets.closed-note') }}
            </p>
        </div>
    </div>

    <div v-else class="tw-p-8 tw-text-center tw-text-gray-400"><icon icon="line-md:loading-twotone-loop" class="tw-text-3xl" /></div>
</template>

<script setup>
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ticket as fetchTicket, replyTicket, ticketsUnreadCount } from '@/api/portal';
import PageHeader from '@/components/PageHeader.vue';
import FormField from '@/components/form/FormField';
import TicketThread from '@/components/TicketThread.vue';
import TicketStatusBadge from '@/views/admin/tickets/TicketStatusBadge.vue';
import { useFormat } from '@/composables/useFormat';
import useAlert from '@/composables/useAlert';
import { usePortalStore } from '@/stores';

const route = useRoute();
const i18n = useI18n();
const fmt = useFormat();
const portalStore = usePortalStore();
const id = route.params.id;

async function refreshUnreadBadge() {
    try {
        const { data } = await ticketsUnreadCount();
        portalStore.setUnreadTickets(data.unread_count);
    } catch (e) { /* non-fatal */ }
}

const ticket = ref({ subject: '', messages: [] });
const loading = ref(true);
const reply = ref('');
const sending = ref(false);

async function load() {
    loading.value = true;
    try {
        const { data } = await fetchTicket(id);
        ticket.value = data.ticket;
        // Viewing marks the ticket read server-side; refresh the sidebar badge.
        refreshUnreadBadge();
    } catch (e) { /* interceptor */ } finally {
        loading.value = false;
    }
}

async function sendReply() {
    sending.value = true;
    try {
        const { data } = await replyTicket(id, { message: reply.value });
        ticket.value = data.ticket;
        reply.value = '';
        useAlert(i18n.t('portal.tickets.reply-sent'));
    } catch (e) { /* interceptor */ } finally {
        sending.value = false;
    }
}

load();
</script>
